// The featured machine (render 03): glass dome of capsules, body, name plate, twist dial and
// GO! button. Drag the dial round (1:1, a detent every 30°, release after ≥ 300°) or tap GO!.
// The capsule rattles, drops down the chute, bounces once, then `onDropped` fires.
import { motion, useAnimate } from 'motion/react'
import { useRef, useState } from 'react'
import { buzz } from '../../../audio/haptics'
import type { CardSetTheme } from '../../../domain/arcade/types'
import type { MotionTier } from '../../../motion/tier'
import { capped } from '../types'

const BODY: Record<CardSetTheme['color'], [string, string, string]> = {
  red: ['#FF6B6B', '#FF4F4F', '#D8343A'],
  mint: ['#7CE6B8', '#36D399', '#1FA874'],
  blue: ['#6EA6FF', '#2F7BFF', '#1F5AD0'],
}
const CAPS = ['cY', 'cB', 'cR', 'cM'] as const
const BALLS: [number, number, number][] = [
  [140, 168, 24], [190, 176, 24], [240, 168, 24], [116, 130, 22], [164, 132, 24], [214, 132, 24],
  [262, 128, 22], [140, 90, 22], [190, 88, 24], [238, 90, 22], [166, 50, 20], [212, 50, 20],
]

export function Gashapon({
  title,
  theme,
  locked,
  tier,
  label,
  onDropped,
}: {
  title: string
  /** Main button text (e.g. "Twist for a capsule"). */
  label: string
  theme: CardSetTheme
  locked: boolean
  tier: MotionTier
  onDropped(): void
}) {
  const [scope, animate] = useAnimate()
  const [angle, setAngle] = useState(-30)
  const [busy, setBusy] = useState(false)
  const drag = useRef<{ last: number; total: number; detents: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const body = BODY[theme.color]
  const still = tier === 'still'

  async function twist() {
    if (locked || busy) return
    setBusy(true)
    buzz('drop')
    if (!still) {
      await capped(async () => {
        await animate('.dome-balls', { rotate: [0, -4, 5, -3, 2, 0] }, { duration: 0.45 })
        await animate('.drop', { opacity: 1, y: [-40, 70] }, { duration: 0.18, ease: 'easeIn' })
        await animate('.drop', { y: [70, 52, 70] }, { type: 'spring', stiffness: 600, damping: 14 })
      }, 1600)
      void animate('.drop', { opacity: 0, y: 0 }, { duration: 0, delay: 0.4 })
    }
    buzz('crack')
    setBusy(false)
    setAngle(-30)
    onDropped()
  }

  const centre = () => {
    const r = svgRef.current!.getBoundingClientRect()
    const s = r.width / 380
    return { x: r.left + 160 * s, y: r.top + 306 * s }
  }
  const angleAt = (e: React.PointerEvent) => {
    const c = centre()
    return (Math.atan2(e.clientY - c.y, e.clientX - c.x) * 180) / Math.PI
  }
  const onDown = (e: React.PointerEvent) => {
    if (locked || busy) return
    ;(e.target as Element).setPointerCapture(e.pointerId)
    drag.current = { last: angleAt(e), total: 0, detents: 0 }
  }
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    const a = angleAt(e)
    let delta = a - d.last
    if (delta > 180) delta -= 360
    if (delta < -180) delta += 360
    d.last = a
    if (delta < 0) return // the dial only turns clockwise
    d.total += delta
    setAngle((x) => x + delta)
    const detents = Math.floor(d.total / 30)
    if (detents > d.detents) {
      d.detents = detents
      buzz('detent')
    }
  }
  const onUp = () => {
    const d = drag.current
    drag.current = null
    if (d && d.total >= 300) void twist()
  }

  return (
    <div ref={scope} className="gashapon">
      <svg ref={svgRef} viewBox="0 0 380 420" role="img" aria-label={`${title} capsule machine${locked ? ', locked' : ''}`}>
        <defs>
          <linearGradient id="gBody" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={body[0]} />
            <stop offset=".5" stopColor={body[1]} />
            <stop offset="1" stopColor={body[2]} />
          </linearGradient>
          <radialGradient id="gGlass" cx=".35" cy=".3" r=".8">
            <stop offset="0" stopColor="#FFFFFF" stopOpacity=".55" />
            <stop offset=".6" stopColor="#DDF0FF" stopOpacity=".12" />
            <stop offset="1" stopColor="#9CCBF0" stopOpacity=".35" />
          </radialGradient>
          <radialGradient id="cY" cx=".35" cy=".3"><stop offset="0" stopColor="#FFF1B8" /><stop offset="1" stopColor="#FFC93C" /></radialGradient>
          <radialGradient id="cB" cx=".35" cy=".3"><stop offset="0" stopColor="#B9D4FF" /><stop offset="1" stopColor="#2F7BFF" /></radialGradient>
          <radialGradient id="cM" cx=".35" cy=".3"><stop offset="0" stopColor="#C9F7E3" /><stop offset="1" stopColor="#36D399" /></radialGradient>
          <radialGradient id="cR" cx=".35" cy=".3"><stop offset="0" stopColor="#FFC2C2" /><stop offset="1" stopColor="#FF4F4F" /></radialGradient>
          <filter id="gGlow"><feDropShadow dx="0" dy="12" stdDeviation="10" floodColor="#000" floodOpacity=".35" /></filter>
        </defs>
        <ellipse cx="190" cy="392" rx="120" ry="10" fill="#000" opacity=".25" />
        <g filter="url(#gGlow)" style={locked ? { filter: 'url(#gGlow) saturate(.55) brightness(.85)' } : undefined}>
          <rect x="128" y="6" width="124" height="22" rx="10" fill="url(#gBody)" stroke="#1B2A3A" strokeWidth="3" />
          <circle cx="190" cy="118" r="98" fill="#E8F5FF" stroke="#1B2A3A" strokeWidth="3.5" />
          <g className="dome-balls" style={{ transformOrigin: '190px 118px' }} stroke="#1B2A3A" strokeWidth="2.5">
            {BALLS.map(([x, y, r], i) => (
              <circle key={i} cx={x} cy={y} r={r} fill={`url(#${CAPS[i % 4]})`} />
            ))}
          </g>
          <circle cx="190" cy="118" r="98" fill="url(#gGlass)" stroke="#1B2A3A" strokeWidth="3.5" />
          <path d="M120,70 a80,80 0 0 1 50,-42" fill="none" stroke="#fff" strokeWidth="10" strokeLinecap="round" opacity=".85" />
          <path d="M96,200 h188 l10,180 h-208z" fill="url(#gBody)" stroke="#1B2A3A" strokeWidth="3.5" strokeLinejoin="round" />
          <rect x="112" y="214" width="156" height="34" rx="12" fill="#fff" stroke="#1B2A3A" strokeWidth="3" />
          <text x="190" y="238" textAnchor="middle" fontFamily="Fredoka" fontWeight="700" fontSize={title.length > 13 ? 16 : 19} fill="#1B2A3A">
            {title.toUpperCase()}
          </text>
          {/* chute + the capsule that drops */}
          <rect x="222" y="290" width="54" height="44" rx="10" fill="#1B2A3A" />
          <g className="drop" style={{ opacity: 0 }}>
            <circle cx="249" cy="290" r="15" fill="url(#cY)" stroke="#1B2A3A" strokeWidth="2.5" />
          </g>
          <rect x="228" y="296" width="42" height="26" rx="8" fill="#2E415A" opacity=".6" />
          {/* dial */}
          <g
            className={`dial${locked ? ' locked' : ''}`}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            role="slider"
            aria-label="Twist the dial"
            aria-valuenow={Math.round(angle)}
          >
            <circle cx="160" cy="306" r="44" fill="#FFF4E6" stroke="#1B2A3A" strokeWidth="3.5" />
            <rect x="152" y="270" width="16" height="72" rx="8" fill="#1B2A3A" transform={`rotate(${angle} 160 306)`} />
            <circle cx="160" cy="306" r="10" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
            {locked ? (
              <g transform="translate(160 306)">
                <rect x="-16" y="-6" width="32" height="26" rx="6" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3" />
                <path d="M-9,-6 v-8 a9,9 0 0 1 18,0 v8" fill="none" stroke="#1B2A3A" strokeWidth="4" />
              </g>
            ) : null}
          </g>
          {/* GO! */}
          <motion.g
            className="go"
            role="button"
            aria-label="Go"
            aria-disabled={locked}
            onClick={() => void twist()}
            whileTap={locked || still ? undefined : { scale: 0.9 }}
            style={{ transformOrigin: '320px 262px', opacity: locked ? 0.55 : 1 }}
          >
            <circle cx="320" cy="262" r="40" fill="#FFC93C" stroke="#1B2A3A" strokeWidth="3.5" />
            <circle cx="320" cy="258" r="31" fill="#FFD866" stroke="#1B2A3A" strokeWidth="2" />
            <text x="320" y="268" textAnchor="middle" fontFamily="Fredoka" fontWeight="700" fontSize="26" fill="#1B2A3A">
              GO!
            </text>
          </motion.g>
        </g>
      </svg>
      <button className="btn y press twist-btn" disabled={locked || busy} onClick={() => void twist()} hidden={locked}>
        {label}
      </button>
    </div>
  )
}
